import { useEffect, useRef } from 'react';
import { usersAPI } from '../services/api';

// "Cost of time" input (VEC_MS_FINANCE_SEEDING_PLAN.md Phase 4) — sends a
// heartbeat roughly every 60s while this tab is open AND the page is
// visible/focused, so the backend can distinguish genuinely-engaged time
// from a token that just happens to still be valid. Paused (not merely
// skipped) while the tab is hidden, per the discovery session's own
// requirement: a backgrounded tab must not silently accrue "active" time.
//
// Mount this once, near the root of the authenticated app shell — not on
// every page — so navigating between pages doesn't reset or duplicate the
// interval.
const HEARTBEAT_INTERVAL_MS = 60_000;

export default function useActivityHeartbeat(isAuthenticated) {
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    const sendHeartbeat = () => {
      if (document.visibilityState !== 'visible') return;
      usersAPI.heartbeat().catch(() => {
        // Best-effort — a missed heartbeat just means a slightly
        // under-counted minute, never worth surfacing to the user.
      });
    };

    const startInterval = () => {
      if (intervalRef.current) return;
      sendHeartbeat(); // count time immediately on becoming visible, not just at the next tick
      intervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
    };

    const stopInterval = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        startInterval();
      } else {
        stopInterval();
      }
    };

    if (document.visibilityState === 'visible') {
      startInterval();
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopInterval();
    };
  }, [isAuthenticated]);
}
